import { formatDistance } from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import {
  grey,
  NO_TRANSLATE_CLASS,
  NoTranslate,
} from "@faro-lotv/app-component-toolbox";
import { Box } from "@mui/material";
import { useMemo, type JSX } from "react";
import { TextLabel, TextLabelProps } from "../text-label";

type MeasureLabelProps = Omit<TextLabelProps, "text"> & {
  /** The the number to show in the label representing the length. */
  distance: number;
  /** Optional symbol that shows before the text string (Eg. X, Y, Z, ...) */
  symbol?: string;
  /**
   * Whether to use small units that make short distances more readable. When enabled,
   * labels will always use mm for metric, fractional inches for imperial.
   */
  useSmallUnits?: boolean;
  /** Flag indicating the value display is an approximation */
  isApproximation?: boolean;
};

/**
 * @returns a Html label that displays a measurement and moves in the 3D scene along with a given 3D position.
 */
export function MeasureLabel({
  index,
  visible,
  position,
  backgroundColor = grey[1000],
  parentRef,
  distance: distanceInMeters,
  symbol,
  labelsScreenPositionsComputer,
  active,
  onClick,
  onPointerDown,
  onContextMenu,
  transparent,
  pointerEvents,
  placement,
  scale,
  useSmallUnits,
  isApproximation,
}: MeasureLabelProps): JSX.Element | null {
  const distanceUnit = useDistanceUnit();

  const distance = useMemo(() => {
    const prefix = isApproximation ? "~" : "";
    // label are should be mm if metric, fractional inches if imperial
    return (
      prefix + formatDistance(distanceInMeters, distanceUnit, { useSmallUnits })
    );
  }, [distanceInMeters, distanceUnit, isApproximation, useSmallUnits]);

  return (
    <TextLabel
      index={index}
      visible={visible}
      position={position}
      parentRef={parentRef}
      labelsScreenPositionsComputer={labelsScreenPositionsComputer}
      active={active}
      onClick={onClick}
      transparent={transparent}
      pointerEvents={pointerEvents}
      placement={placement}
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
      scale={scale}
      backgroundColor={backgroundColor}
    >
      {symbol && (
        <Box
          component="span"
          sx={{ opacity: 0.5 }}
          className={NO_TRANSLATE_CLASS}
        >
          {`${symbol}\u00A0`}
        </Box>
      )}
      <NoTranslate component="span">{distance}</NoTranslate>
    </TextLabel>
  );
}
